import { Button, Group, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { IconFolderPlus, IconUpload } from '@tabler/icons-react';
import { matchesAllTokens } from 'src/common/utils/textSearch';
import { buildFolderPath } from 'src/data-capture/file-management/utils/buildFolderPath';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { EntityWithFile, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import FilesTable from 'src/data-capture/file-management/files-table/FilesTable';
import MoveFileModal from 'src/data-capture/file-management/files-table/MoveFileModal';
import { CreateFolderModal } from 'src/data-capture/file-management/modals/CreateFolderModal';
import { RenameFolderModal } from 'src/data-capture/file-management/modals/RenameFolderModal';
import CompanyFilesLayout from './CompanyFilesLayout';
import { useCompanyFiles } from '../hooks/useCompanyFiles';
import { useCompanyFileActions } from '../hooks/useCompanyFileActions';

export default function CompanyFilesPage() {
  const { folderId: folderIdParam } = useParams<{ folderId?: string }>();
  const navigate = useNavigate();

  const currentFolder = folderIdParam ?? null;
  const [searchValue, setSearchValue] = useState('');
  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);

  const { files, isLoading: isLoadingFiles, error, refetchFiles } = useCompanyFiles();

  const {
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    createFolder,
    moveFile,
    renameFolder,
  } = useCompanyFileActions();

  const {
    createLink,
    isLoading: isCreatingLink,
    isError: creatingLinkFailed,
  } = useFileTemporaryLinkMutation();

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const handleDownloadFile = async (uploadedFile: UploadedFile, anchor?: HTMLAnchorElement) => {
    try {
      const link = await createLink(uploadedFile.file.id);
      if (anchor) {
        anchor.href = link.url;
        anchor.download = uploadedFile.file.fileName || 'download';
        anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } else {
        window.open(link.url, '_blank');
      }
    } catch (downloadError) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        downloadError,
        { fileId: uploadedFile.file.id },
      );
    }
  };

  const handleFolderClick = (folder: EntityWithFile) => {
    setSearchValue('');
    navigate(`/company/documents/folder/${folder.id}`);
  };

  const handleMovePress = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (moveError) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        moveError,
        { fileId: file.id, parentId },
      );
    }
  };

  const getCurrentFolderName = () => {
    if (!currentFolder) {
      return 'All company documents';
    }
    const folder = files.find(f => f.id === currentFolder && f.isFolder);
    return folder?.file.fileName || 'Unknown folder';
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolder(folderName, currentFolder ?? undefined);
      await refetchFiles();
    } catch (createError) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        createError,
        { folderName, currentFolder },
      );
      throw createError;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleRenameFolder = async (folder: UploadedFile, newName: string) => {
    try {
      await renameFolder(folder, newName);
      await refetchFiles();
    } catch (renameError) {
      notifyAndLogError(
        'Failed to rename folder',
        'An error occurred while trying to rename the folder. Please try again later.',
        renameError,
        { folderId: folder.id },
      );
      throw renameError;
    }
  };

  const handleUploadClick = () => {
    const base = '/company/documents/add';
    navigate(currentFolder ? `${base}?parentId=${currentFolder}` : base);
  };

  const filesByFolder = useMemo(
    () =>
      files.reduce((acc: { [parentId: string]: UploadedFile[] }, uploadedFile) => {
        const key = uploadedFile.parentId || 'root';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(uploadedFile);
        return acc;
      }, {}),
    [files],
  );

  const isSearching = searchValue.trim().length > 0;

  const filteredFiles = useMemo(() => {
    if (!isSearching) {
      return filesByFolder?.[currentFolder || 'root'] || [];
    }

    return files.filter(file =>
      matchesAllTokens(`${buildFolderPath(file, files)} ${file.file.fileName}`, searchValue),
    );
  }, [files, filesByFolder, currentFolder, searchValue, isSearching]);

  const searchColumns = useMemo(
    () =>
      isSearching
        ? [
            {
              label: 'Location',
              valueBuilder: (file: UploadedFile) => (
                <Text size="sm" c="dimmed">
                  {buildFolderPath(file, files) || 'All company documents'}
                </Text>
              ),
            },
          ]
        : [],
    [isSearching, files],
  );

  if (error || creatingLinkFailed) {
    return <GenericErrorPage error={error ?? new Error('Failed to create temporary file link')} />;
  }

  if (isCreatingLink) {
    return <PageLoader absolute={false} />;
  }

  return (
    <CompanyFilesLayout
      title={getCurrentFolderName()}
      showSearch
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={isSearching ? filteredFiles.length : undefined}
      actions={
        <Group gap="sm" wrap="nowrap">
          <Button
            variant="secondary"
            size="md"
            onClick={openCreateFolder}
            aria-label="Create folder"
            title="Create folder"
            px="xs"
          >
            <IconFolderPlus size={16} />
          </Button>
          <Button
            variant="primary"
            size="md"
            leftSection={<IconUpload size={16} />}
            onClick={handleUploadClick}
          >
            Upload
          </Button>
        </Group>
      }
    >
      <FilesTable<UploadedFile>
        entitiesWithFile={filteredFiles}
        isLoading={isLoadingFiles}
        additionalColumns={searchColumns}
        onClick={(file: EntityWithFile) => (file.isFolder ? handleFolderClick(file) : null)}
        enableDragDrop
        actions={(file: UploadedFile) => {
          if (file.isFolder) {
            return [
              { label: 'Rename', action: handleRenameFolderClick },
              { label: 'Move', action: handleMovePress },
              { label: 'Delete', action: handleDeletePress },
            ];
          }
          return [
            { label: 'Download', action: handleDownloadFile },
            { label: 'Move', action: handleMovePress },
            { label: 'Delete', action: handleDeletePress },
          ];
        }}
      />

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        onCreateFolder={handleCreateFolder}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel="All company documents"
          onFileMoved={handleMoveFile}
        />
      )}

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={handleRenameFolder}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />
    </CompanyFilesLayout>
  );
}
